import { SCREEN_SIZES } from '@/constants/screen-size';
import {
  DesktopScreenSize,
  MobileScreenSize,
  ScreenSize,
  TabletScreenSize,
} from '@/types/ScreenSize';

let screenSizeWidth = 0;
let screenSizeName: ScreenSize['name'] | undefined = undefined;
let listenerDebounce: number | undefined = undefined;

const screenSizeListeners: ((screenSize: ScreenSize) => void)[] = [];

export const registerScreenSizeObserver = () => {
  const observer = new ResizeObserver(([entry]) => {
    if (screenSizeWidth !== entry.contentRect.width) {
      screenSizeWidth = entry.contentRect.width;

      window.clearTimeout(listenerDebounce);

      // Fire change in screen size to listeners
      listenerDebounce = window.setTimeout(() => {
        const screenSize = getScreenSize() as ScreenSize;

        if (screenSizeName !== screenSize.name) {
          screenSizeName = screenSize.name as keyof typeof SCREEN_SIZES;

          screenSizeListeners.forEach((screenSizeListener) => {
            screenSizeListener(screenSize);
          });
        }
      }, 250);
    }
  });

  observer.observe(document.documentElement);

  return () => {
    observer.disconnect();
  };
};

export const registerScreenSizeListener = (listener: (typeof screenSizeListeners)[number]) => {
  screenSizeListeners.push(listener);

  return () => {
    screenSizeListeners.splice(screenSizeListeners.indexOf(listener), 1);
  };
};

export const getScreenSizeByPixels = (pixels: number) => {
  const screenSizeEntries = Object.entries(SCREEN_SIZES).sort(
    ([, a], [, b]) => b.minPixels - a.minPixels,
  );

  const [, screenSize] =
    screenSizeEntries.find(([, screenSize]) => {
      return pixels >= screenSize.minPixels;
    }) || screenSizeEntries[0];

  return screenSize;
};

export const getScreenSize = () => {
  return getScreenSizeByPixels(screenSizeWidth || window.innerWidth);
};

export const getScreenSizeName = () => {
  return screenSizeName;
};

export const getLayoutType = (pixels?: number) => {
  return pixels ? getScreenSizeByPixels(pixels).layoutType : getScreenSize().layoutType;
};

export const isMobileScreenSize = (screenSize?: ScreenSize): screenSize is MobileScreenSize => {
  const { name } = screenSize || getScreenSize();

  const isMobile =
    [SCREEN_SIZES.MOBILE.name].findIndex((mobileScreenSizeName) => mobileScreenSizeName === name) >
    -1;

  return isMobile;
};

export const isTabletScreenSize = (screenSize?: ScreenSize): screenSize is TabletScreenSize => {
  const { name } = screenSize || getScreenSize();

  return SCREEN_SIZES.TABLET.name === name;
};

export const isDesktopScreenSize = (screenSize?: ScreenSize): screenSize is DesktopScreenSize => {
  const { name } = screenSize || getScreenSize();

  return SCREEN_SIZES.DESKTOP.name === name;
};
